import React, { useState, useEffect } from "react";
import DoneIcon from "@mui/icons-material/Done";
import CircleIcon from "@mui/icons-material/Circle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import ThemePreview from "./ThemePreview";
import { colorOptions, fontOptions, defaultTheme } from "./themeConstants";
import CommonButtons from "../../../components/CommonButtons";
import { notifyError, notifySuccess } from "../../../../assets/toastService";
import { Theme } from "../types/profile";

interface ThemeCustomizerProps {
  theme: Theme | null;
  logo: string;
  userName: string;
  businessName: string;
  onUpdateTheme: (theme: Theme) => Promise<{ success: boolean }>;
  onResetTheme: () => Promise<{ success: boolean }>;
}

const ThemeCustomizer: React.FC<ThemeCustomizerProps> = ({ theme, logo, userName, businessName, onUpdateTheme, onResetTheme }) => {
  const [selectedBackground, setSelectedBackground] = useState(defaultTheme.backgroundColor);
  const [selectedFontColor, setSelectedFontColor] = useState(defaultTheme.fontColor);
  const [selectedFontFamily, setSelectedFontFamily] = useState(defaultTheme.fontFamily);

  useEffect(() => {
    if (theme) {
      setSelectedBackground(theme.backgroundColor);
      setSelectedFontColor(theme.fontColor);
      setSelectedFontFamily(theme.fontFamily);
    }
  }, [theme]);

  const handleColorSelect = (colorKey: string) => {
    setSelectedBackground(colorKey);
    const classString = colorOptions[colorKey];
    const textClass = classString.match(/text-(#[0-9A-Fa-f]{6}|\w+)/)?.[1] || "white";
    setSelectedFontColor(textClass);
  };

  const handleFontSelect = (fontKey: string) => setSelectedFontFamily(fontKey);

  const handleUpdate = async () => {
    const newTheme: Theme = {
      backgroundColor: selectedBackground,
      fontColor: selectedFontColor,
      fontFamily: selectedFontFamily,
      businessName,
    };
    const result = await onUpdateTheme(newTheme);
    result.success ? notifySuccess("Theme updated successfully!") : notifyError("Failed to update theme.");
    window.location.reload();
  };

  const handleReset = async () => {
    setSelectedBackground(defaultTheme.backgroundColor);
    setSelectedFontColor(defaultTheme.fontColor);
    setSelectedFontFamily(defaultTheme.fontFamily);
    const result = await onResetTheme();
    result.success ? notifySuccess("🔄 Theme reset to default!") : notifyError("Failed to reset theme.");
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 overflow-auto h-full p-1 sm:p-2">
      {/* Left: Colors & Fonts */}
      <div className="w-full lg:w-3/5 space-y-6">
        {/* Colors */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-300 pb-1">Theme Colors</h3>
          <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-3">
            {Object.entries(colorOptions).map(([name, className]) => (
              <div
                key={name}
                className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg cursor-pointer transition-transform hover:scale-105 shadow-sm flex items-center justify-center ${className}`}
                onClick={() => handleColorSelect(name)}
              >
                {selectedBackground === name && <DoneIcon className="text-white text-2xl drop-shadow-sm" />}
              </div>
            ))}
          </div>
        </div>

        {/* Font Style */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-300 pb-1">Font Styles</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[220px] overflow-y-auto pr-1">
            {Object.entries(fontOptions).map(([name, className]) => (
              <div
                key={name}
                className={`flex items-center p-2.5 border rounded-lg cursor-pointer transition-all duration-150 ${
                  selectedFontFamily === name ? "bg-blue-50 border-blue-300" : "border-gray-200 hover:bg-gray-50"
                } ${className}`}
                onClick={() => handleFontSelect(name)}
              >
                {selectedFontFamily === name ? <CircleIcon className="text-blue-600 mr-2 text-sm" /> : <CircleOutlinedIcon className="text-gray-400 mr-2 text-sm" />}
                <span className="text-sm">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Preview & Actions */}
      <div className="w-full lg:w-2/5 space-y-4">
        <ThemePreview
          theme={{
            backgroundColor: selectedBackground,
            fontColor: selectedFontColor,
            fontFamily: selectedFontFamily,
            businessName,
          }}
          logo={logo}
          userName={userName}
          businessName={businessName}
        />
        <div className="flex flex-wrap justify-center gap-3">
          <CommonButtons label="Reset Theme" onClick={handleReset} variant="secondary" />
          <CommonButtons label="Update Theme" onClick={handleUpdate} />
        </div>
      </div>
    </div>
  );
};

export default ThemeCustomizer;
